import React, { useState } from 'react';
import { 
  Search, 
  Star, 
  ArrowRight, 
  CreditCard, 
  Package, 
  TrendingUp, 
  MessageSquare, 
  Layers,
  Zap,
  Smartphone,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import StoreNavbar from '@/layouts/StoreNavbar/StoreNavbar';

const categories = [
  { id: 'todos', label: 'Todos' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'vendas', label: 'Canais de vendas' },
  { id: 'pagamentos', label: 'Pagamentos' },
  { id: 'logistica', label: 'Logística' },
  { id: 'gestao', label: 'Gestão' },
  { id: 'seguranca', label: 'Segurança' },
  { id: 'comunicacao', label: 'Comunicação' },
];

const apps = [
  {
    id: 1, name: 'Lukos Pay', category: 'pagamentos',
    desc: 'Pagamentos integrados com conciliação automática e as melhores taxas do mercado.',
    icon: CreditCard, color: '#8200FF', badge: 'Smart POS',
    rating: 4.9, reviews: 312,
    tags: ['Cartão', 'PIX', 'Conciliação'],
    heroImage: '/lukos_pay_app_mockup_1777575710338.png'
  },
  {
    id: 2, name: 'Inventário', category: 'gestao',
    desc: 'Controle total do inventário com alertas de ruptura e reposição automática.',
    icon: Package, color: '#0ea5e9', badge: 'Disponível agora',
    rating: 4.7, reviews: 264,
    tags: ['Estoque', 'Alertas', 'Relatórios'],
    heroImage: '/lukos_inventory_app_mockup_1777575732671.png'
  },
  {
    id: 3, name: 'Lukos Prevenda', category: 'vendas',
    desc: 'Agilize o atendimento no pátio e na conveniência com lançamentos de comandas via mobile.',
    icon: MessageSquare, color: '#8200FF', badge: 'Popular',
    rating: 4.9, reviews: 421,
    iconImage: 'https://play-lh.googleusercontent.com/iopYAcByWMi9_OxQM2lsVtIqtlpoI0MoYdmoNa1YOvC0fEZlIxGx5C4IIjUrRAXDr2',
    heroImage: 'https://play-lh.googleusercontent.com/iopYAcByWMi9_OxQM2lsVtIqtlpoI0MoYdmoNa1YOvC0fEZlIxGx5C4IIjUrRAXDr2w=w2560-h1440-rw',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.wendev.lukoscomandas'
  },
  {
    id: 4, name: 'Luk IA', category: 'gestao',
    desc: 'Inteligência artificial que analisa padrões e antecipa tendências do seu negócio.',
    icon: TrendingUp, color: '#6366f1', badge: 'Disponível agora',
    rating: 4.8, reviews: 187,
    tags: ['IA', 'Previsão', 'Automação'],
    heroImage: '/luk_ia_app_mockup_1777575757204.png'
  },
  {
    id: 5, name: 'Frente de Caixa PDV', category: 'vendas',
    desc: 'O PDV mais rápido do segmento. Checkout ágil com integração fiscal completa.',
    icon: Layers, color: '#10b981', badge: null,
    rating: 4.9, reviews: 528,
    tags: ['PDV', 'NFC-e', 'Offline'],
    heroImage: '/lukos_inventory_app_mockup_1777575732671.png'
  },
];

const AppsPage = () => {
  const [activeCategory, setActiveCategory] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = apps.filter(app => {
    const matchCat = activeCategory === 'todos' || app.category === activeCategory;
    const matchSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.desc.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="bg-white min-h-screen font-inter overflow-x-hidden selection:bg-purple-100 selection:text-[#8200FF]">
      <StoreNavbar />

      {/* Featured Showcase Section */}
      <section className="relative min-h-[85vh] flex flex-col items-center justify-center pt-24 pb-20 px-6 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/lukos_inventory_dark_hero_bg_1777575434518.png" 
            alt="Showcase Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full flex flex-col items-center">
          {/* Main Content */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center text-center gap-10 max-w-4xl"
          >


            <h1 className="text-white text-[64px] md:text-[104px] font-extrabold leading-[0.85] tracking-[-0.06em] font-inter">
              Um aplicativo para <br />
              <span className="text-[#8200FF] drop-shadow-[0_0_45px_rgba(130,0,255,0.6)]">cada solução.</span>
            </h1>

            <p className="text-white/60 text-lg md:text-2xl font-medium max-w-3xl leading-relaxed font-inter">
              Simplifique sua gestão com ferramentas sob medida. Descubra nossa vitrine de aplicativos 
              desenhados para resolver desafios específicos da sua operação com precisão e agilidade.
            </p>

            {/* Solutions Ribbon */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mt-4 w-full max-w-3xl">
              {[
                { icon: Smartphone, label: 'PDV Móvel', color: '#10b981' },
                { icon: Package, label: 'Gestão Estoque', color: '#0ea5e9' },
                { icon: Zap, label: 'Luk IA Data', color: '#8200FF' },
                { icon: CreditCard, label: 'Lukos Pay', color: '#f59e0b' }
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + (i * 0.1) }}
                  className="bg-white/5 backdrop-blur-md border border-white/10 p-4 rounded-2xl flex flex-col items-center gap-3 hover:bg-white/10 transition-all cursor-default group"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <item.icon size={20} style={{ color: item.color }} />
                  </div>
                  <span className="text-white/70 text-[11px] font-bold uppercase tracking-wider">{item.label}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-6 mt-8">
              <button className="bg-[#8200FF] text-white font-black text-sm uppercase tracking-widest px-12 py-6 rounded-full transition-all flex items-center gap-3 shadow-[0_20px_50px_rgba(130,0,255,0.4)] hover:scale-105 active:scale-95">
                Ver Todos os Apps
                <ArrowRight size={20} />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Decorative elements removed to maintain dark theme */}
      </section>

      {/* Apps Section */}
      <section className="px-6 py-20 bg-white" id="marketplace">
        <div className="max-w-7xl mx-auto">
          
          {/* Categories Filter */}
          <div className="flex items-center gap-3 overflow-x-auto mb-12 scrollbar-hide">
            {[
              'Todos', 'PDV & Frente de Caixa', 'Conciliação Financeira', 
              'Estoque & Logística', 'Luk IA', 'WhatsApp & CRM', 
              'Segurança & Auditoria', 'Pagamentos', 'Relatórios', 'Controle de Preços'
            ].map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat.toLowerCase())}
                className={`whitespace-nowrap px-8 py-4 rounded-full text-[12px] font-bold transition-all border ${
                  activeCategory === cat.toLowerCase()
                    ? 'bg-[#8200FF] text-white border-[#8200FF] shadow-2xl shadow-purple-500/30 scale-105'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-purple-300 hover:text-purple-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory + searchQuery}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
            >
              {filtered.length > 0 ? filtered.map((app, index) => {
                const Icon = app.icon;
                const brandColor = app.color;
                
                return (
                  <motion.div
                    key={app.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="group bg-white rounded-[32px] overflow-hidden transition-all duration-500 cursor-pointer flex flex-col border border-slate-100 hover:border-purple-200 hover:shadow-[0_30px_60px_rgba(130,0,255,0.12)] hover:-translate-y-1"
                  >
                    {/* App Hero Area */}
                    <div className="relative h-52 overflow-hidden bg-slate-50">
                      {app.heroImage ? (
                        <img 
                          src={app.heroImage} 
                          alt={app.name} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                        />
                      ) : (
                        <div className="w-full h-full bg-linear-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                           <Icon size={48} className="text-slate-300" />
                        </div>
                      )}
                      
                      {app.badge && (
                        <div className="absolute top-4 right-4 z-20">
                          <span className="bg-[#8200FF] text-white text-[10px] font-black px-3.5 py-1.5 rounded-full uppercase tracking-widest shadow-xl">
                            {app.badge}
                          </span>
                        </div>
                      )}
                      
                      <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>

                    {/* App Info Area */}
                    <div className="p-7 flex flex-col gap-6 flex-1">
                      <div className="flex gap-4 items-start">
                        {/* App Icon Container */}
                        <div className="relative w-16 h-16 rounded-[22px] shadow-md border border-slate-100 flex items-center justify-center bg-white overflow-hidden shrink-0 group-hover:rotate-3 transition-transform duration-500">
                          {app.iconImage ? (
                            <img src={app.iconImage} alt={app.name} className="w-full h-full object-cover" />
                          ) : (
                            <Icon size={32} style={{ color: brandColor }} />
                          )}
                        </div>
                        
                        <div className="flex flex-col pt-1">
                          <h3 className="text-[#0f172a] font-black text-[20px] leading-tight tracking-tight">
                            {app.name}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-0.5">
                              <span className="text-[14px] font-black text-[#0f172a]">{app.rating}</span>
                              <Star size={12} className="text-[#8200FF] fill-[#8200FF]" />
                            </div>
                            <span className="text-[12px] text-slate-400 font-bold uppercase tracking-widest">• Lukos</span>
                          </div>
                        </div>
                      </div>

                      <p className="text-slate-500 text-[14px] line-clamp-2 leading-relaxed font-medium">
                        {app.desc}
                      </p>

                      {/* Install Button - Refined pill style */}
                      <button
                        className="w-full h-14 bg-[#8200FF] text-white font-black text-[12px] uppercase tracking-[0.2em] rounded-2xl hover:bg-[#6e00d6] shadow-lg shadow-purple-500/20 transition-all active:scale-95 mt-auto flex items-center justify-center gap-2 group/btn"
                      >
                        OBTER
                        <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </motion.div>
                );
              }) : (
                <div className="col-span-full text-center py-32 flex flex-col items-center gap-4">
                  <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center text-slate-200">
                    <Search size={48} />
                  </div>
                  <p className="text-slate-400 font-black text-2xl">Nenhum aplicativo encontrado</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

    </div>
  );
};

export default AppsPage;
